import {
  Alert,
  Button,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import { SongbookUb } from "../../gql/graphql";
import { useRef, useState } from "react";
import Select, { SelectInstance } from "react-select";
import { ValueAndLabel } from "./SelectGraphQlMutate";
import { useHotkeys } from "react-hotkeys-hook";
import { CyItem } from "./CyItem";
import { useSongnumbersBySongbookQuery } from "./useSongnumbersBySongbookQuery";
import { useTranslation } from "react-i18next";

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onConfirm: (songbookId: string, number: string) => void;
  availableSongbooks: readonly SongbookUb[];
  errorMessage?: string;
  editMode?: {
    songbookId: string;
    number: string;
  };
  bucket: string;
}

export function AddSongnumberModal({
  isOpen,
  onCancel,
  onConfirm,
  availableSongbooks,
  errorMessage,
  editMode,
  bucket,
}: Props) {
  const { t } = useTranslation();
  const [selectedSongbookId, setSelectedSongbookId] = useState<string | null>(
    editMode?.songbookId ?? null,
  );
  const [number, setNumber] = useState<string>(editMode?.number ?? "");
  const inputRef = useRef<HTMLInputElement>(null);
  const selectRef = useRef<SelectInstance<ValueAndLabel>>(null);

  // Query for existing songnumbers in the selected songbook
  const { songnumbers, fetching } = useSongnumbersBySongbookQuery(
    bucket,
    selectedSongbookId,
  );

  const songbookOptions: ValueAndLabel[] = availableSongbooks.map((sb) => ({
    value: sb.id,
    label: `${sb.name} (${sb.mnemonic})`,
  }));

  const handleConfirm = () => {
    if (selectedSongbookId && number.trim()) {
      onConfirm(selectedSongbookId, number.trim());
    }
  };

  const handleCancel = () => {
    // Reset form
    setSelectedSongbookId(null);
    setNumber("");
    onCancel();
  };

  const handleModalOpened = () => {
    if (editMode) {
      setSelectedSongbookId(editMode.songbookId);
      setNumber(editMode.number);
      inputRef.current?.focus();
    } else {
      setSelectedSongbookId(null);
      setNumber("");
      selectRef.current?.focus();
    }
  };

  const isValid = selectedSongbookId !== null && number.trim() !== "";
  const isEditMode = editMode !== undefined;

  // Hotkeys: Enter to save, Escape to cancel
  useHotkeys(
    "enter",
    (e) => {
      e.preventDefault();
      if (isValid) {
        handleConfirm();
      }
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  useHotkeys(
    "escape",
    (e) => {
      e.preventDefault();
      handleCancel();
    },
    {
      enableOnFormTags: ["input"],
      enabled: isOpen,
    },
  );

  return (
    <Modal
      size="lg"
      scrollable={true}
      fullscreen="md"
      isOpen={isOpen}
      toggle={handleCancel}
      onOpened={handleModalOpened}
      data-cy={CyItem.SongnumberModal}
    >
      <ModalHeader toggle={handleCancel}>
        {isEditMode
          ? t("items.songnumbers.modalTitleEdit")
          : t("items.songnumbers.modalTitleAdd")}
      </ModalHeader>
      <div
        style={{
          padding: "var(--bs-modal-padding)",
          borderBottom:
            "var(--bs-modal-footer-border-width) solid var(--bs-modal-footer-border-color)",
        }}
      >
        {errorMessage && (
          <Alert color="danger" fade={false}>
            {errorMessage}
          </Alert>
        )}
        <FormGroup>
          <Label>{t("items.songnumbers.songbookLabel")}</Label>
          <Select
            ref={selectRef}
            options={songbookOptions}
            value={
              songbookOptions.find((o) => o.value === selectedSongbookId) ??
              null
            }
            onChange={(option) => setSelectedSongbookId(option?.value ?? null)}
            placeholder={t("items.songnumbers.songbookPlaceholder")}
            isClearable
            isDisabled={isEditMode}
          />
        </FormGroup>
        <FormGroup>
          <Label htmlFor="songnumber">
            {t("items.songnumbers.numberLabel")}
          </Label>
          <Input
            innerRef={inputRef}
            id="songnumber"
            type="text"
            value={number}
            maxLength={8}
            onChange={(e) => setNumber(e.target.value)}
            placeholder={t("items.songnumbers.numberPlaceholder")}
          />
        </FormGroup>
        {t("items.songnumbers.alreadyAssigned")}
      </div>
      <ModalBody>
        {selectedSongbookId && (
          <>
            {fetching ? (
              <div className="text-muted">{t("common.loadingText")}</div>
            ) : songnumbers.length > 0 ? (
              <div className="d-flex flex-column gap-1">
                {songnumbers.map((sn) => (
                  <div
                    key={sn.mnemonicNumber}
                    className="d-flex align-items-center py-1"
                  >
                    <span
                      className="text-muted"
                      style={{ width: "70px", flexShrink: 0 }}
                    >
                      {sn.mnemonicNumber}
                    </span>
                    <span className="text-muted">{sn.songTitle}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-muted mt-1">
                {t("items.songnumbers.noneAssigned")}
              </div>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={handleCancel}>
          {t("common.buttons.cancel")}
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={!isValid}
          data-cy={CyItem.SongnumberModalConfirm}
        >
          {isEditMode ? t("common.buttons.save") : t("common.buttons.add")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
